import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, History, AlertCircle, BarChart3,
  Settings, Flame, LogOut, Shield, TrendingUp, UsersRound, Download,
  KeyRound, User, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { changePassword, updateProfile, exportExcel } from "@/lib/api";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/09a62dc9-6a9e-4bad-b528-41b496ab2382/images/cbcacb73a9857c56eaa830e6342d9e2d9c033cf8fd7db1c24131862abae98730.png";

const ROLE_LABELS = { admin: "Admin", accountant: "Accountant", delivery: "Delivery", viewer: "Viewer" };

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, roles: null },
  { path: "/customers", label: "Customers", icon: Users, roles: null },
  { path: "/bills/generate", label: "Generate Bill", icon: FileText, roles: ["admin", "accountant"] },
  { path: "/bills/history", label: "Bill History", icon: History, roles: null },
  { path: "/dues", label: "Dues Tracker", icon: AlertCircle, roles: ["admin", "accountant"] },
  { path: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "accountant", "viewer"] },
  { path: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export default function Sidebar({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, hasRole, refreshUser } = useAuth() || {};
  const [pwOpen, setPwOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [profileForm, setProfileForm] = useState({ name: user?.name || "", phone: user?.phone || "" });

  const handleChangePw = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error("Passwords don't match"); return; }
    if (pwForm.newPassword.length < 4) { toast.error("Password too short"); return; }
    try {
      await changePassword(pwForm.currentPassword, pwForm.newPassword);
      setPwOpen(false);
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); }
  };

  const handleUpdateProfile = async () => {
    try {
      await updateProfile(profileForm);
      if (refreshUser) await refreshUser();
      setProfileOpen(false);
      toast.success("Profile updated");
    } catch { toast.error("Failed to update profile"); }
  };

  const handleExcelExport = async () => {
    try {
      await exportExcel();
      toast.success("Excel exported");
    } catch { toast.error("Export failed"); }
  };

  const visibleNav = navItems.filter((item) => !item.roles || (user && item.roles.includes(user.role)));

  return (
    <div className="w-64 h-full bg-zinc-950 text-zinc-400 flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-zinc-800">
        <img src={LOGO_URL} alt="Superflame Gas Services" className="h-10 mb-2" />
        <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-[0.2em]">Gas Billing System</p>
      </div>

      <nav className="flex-1 py-4" data-testid="sidebar-nav">
        {visibleNav.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => { navigate(item.path); onNavigate?.(); }}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-all duration-150 ${
                isActive ? "border-l-2 border-orange-600 bg-zinc-900/50 text-white font-semibold" : "border-l-2 border-transparent hover:bg-zinc-900/30 hover:text-zinc-200"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-zinc-800 space-y-2">
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-dropdown-trigger" className="w-full flex items-center gap-2 px-2 py-2 text-xs hover:bg-zinc-900/30 rounded-sm transition-colors text-left">
                <div className="w-7 h-7 bg-orange-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-200 font-medium truncate text-xs">{user.name || user.email}</p>
                  <p className="text-zinc-500 text-[10px]">{ROLE_LABELS[user.role] || user.role}</p>
                </div>
                <ChevronDown className="w-3 h-3 text-zinc-500 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" side="top">
              <DropdownMenuItem data-testid="menu-profile" onClick={() => { setProfileForm({ name: user.name || "", phone: user.phone || "" }); setProfileOpen(true); }}>
                <User className="w-4 h-4 mr-2" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-change-password" onClick={() => setPwOpen(true)}>
                <KeyRound className="w-4 h-4 mr-2" /> Change Password
              </DropdownMenuItem>
              {hasRole?.("admin") && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-audit-log" onClick={() => { navigate("/audit-log"); onNavigate?.(); }}>
                    <Shield className="w-4 h-4 mr-2" /> Audit Log
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-rate-settings" onClick={() => { navigate("/rate-settings"); onNavigate?.(); }}>
                    <TrendingUp className="w-4 h-4 mr-2" /> Rate Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-manage-users" onClick={() => { navigate("/manage-users"); onNavigate?.(); }}>
                    <UsersRound className="w-4 h-4 mr-2" /> Manage Users
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-export-excel" onClick={handleExcelExport}>
                    <Download className="w-4 h-4 mr-2" /> Export All Data
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={async () => { if (logout) await logout(); onNavigate?.(); }} className="text-red-500 focus:text-red-500">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="flex items-center gap-2 text-zinc-600 pt-1">
          <Flame className="w-4 h-4 text-orange-600" />
          <span className="text-xs font-mono">SUPERFLAME v2.0</span>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold">Change Password</DialogTitle><DialogDescription>Enter your current and new password.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Current Password</Label><Input data-testid="current-pw-input" type="password" value={pwForm.currentPassword} onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">New Password</Label><Input data-testid="new-pw-input" type="password" value={pwForm.newPassword} onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input data-testid="confirm-pw-input" type="password" value={pwForm.confirmPassword} onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="save-pw-button" onClick={handleChangePw} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">Change Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader><DialogTitle className="font-heading font-bold">My Profile</DialogTitle><DialogDescription>Update your name and phone.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Name</Label><Input data-testid="profile-name-input" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Phone</Label><Input data-testid="profile-phone-input" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Role</Label><Input value={ROLE_LABELS[user?.role] || user?.role || ""} disabled className="bg-zinc-100 border-zinc-300 rounded-sm mt-1 cursor-not-allowed" /></div>
          </div>
          <DialogFooter><Button data-testid="save-profile-button" onClick={handleUpdateProfile} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
